import { useMutation } from "@tanstack/react-query";
import { actions } from "astro:actions";
import { useState } from "react";

import { useLocale } from "~/hooks/locale";
import { useCreateQueryClient } from "~/hooks/query";
import { trackEvent } from "~/utils/analytics";

import { DialogBody } from "../DialogBody";
import { DialogFooter } from "../DialogFooter";
import { DialogHeader } from "../DialogHeader";
import { DialogTitle } from "../DialogTitle";
import { Fork } from "../Fork";
import { Textarea } from "../Textarea";

export const FeedbackForm: React.FC = () => {
  const { t } = useLocale();
  const [rating, setRating] = useState<string | null>(null);

  const queryClient = useCreateQueryClient();
  const { mutate, isSuccess, isPending } = useMutation(
    {
      mutationFn: actions.discord.submitFeedback.orThrow,
      onSuccess: () => {
        if (rating) {
          trackEvent("feedback_submit", { rating });
        }
      },
    },
    queryClient,
  );

  if (isSuccess) {
    return (
      <DialogBody className="flex aspect-video">
        <div className="m-auto text-center text-2xl title">
          {t("feedback_success")}
        </div>
      </DialogBody>
    );
  }

  return (
    <form
      className="contents"
      onSubmit={(event) => {
        event.preventDefault();

        const url = new URL(window.location.href);
        const formData = new FormData(event.currentTarget);

        formData.append("path", url.pathname);
        url.searchParams.forEach((value, key) => {
          formData.append(key, value);
        });

        mutate(formData);
      }}
    >
      <DialogHeader>{t("feedback_header")}</DialogHeader>
      <DialogBody className="space-y-6">
        <DialogTitle>{t("feedback_title")}</DialogTitle>
        <div className="grid grid-cols-4 gap-4">
          {["🙁", "😐", "😊", "🥳"].map((emoji, index) => (
            <Fork
              type="button"
              key={index}
              aria-selected={rating === emoji}
              className="aspect-square text-5xl"
              onClick={() => {
                setRating(emoji);
              }}
            >
              {emoji}
            </Fork>
          ))}
        </div>
        {rating !== null && (
          <>
            <input type="hidden" name="rating" value={rating} />
            <Textarea
              name="comments"
              label={t("feedback_label")}
              placeholder={t("feedback_placeholder")}
              maxLength={300}
            />
          </>
        )}
      </DialogBody>
      <DialogFooter>
        <button
          disabled={isPending || rating === null}
          type="submit"
          className="btn btn-secondary btn-block"
        >
          {t("feedback_submit")}
        </button>
      </DialogFooter>
    </form>
  );
};
